/**
 * ChartFrame — the shared, token-driven SVG chart/schematic container for the
 * design screens (depth-vs-days plan curve W5, well/casing schematic W2/W3,
 * trajectory views W6, cost-by-phase bars W4). Deliberately dependency-free:
 * the design doc calls for hand-SVG schematics (reusing OXBO-x compute math),
 * so this owns the responsive viewBox, padding, title and axes, and hands the
 * caller a plotting area with value→pixel scales. No external chart lib, no
 * external refs — CSP-safe and self-contained.
 */
import { useId, type ReactNode } from "react";

export interface ChartScales {
  /** Inner plot rectangle (after padding), in SVG user units. */
  plot: { x: number; y: number; w: number; h: number };
  /** Map a domain value on X/Y to a pixel coordinate inside the plot. */
  sx: (v: number) => number;
  sy: (v: number) => number;
}

export interface ChartFrameProps {
  title?: ReactNode;
  /** Domain extents; if omitted the axes render 0..1 (caller supplies its own). */
  xDomain?: [number, number];
  yDomain?: [number, number];
  /** Whether Y grows downward (depth charts) — default false (math up). */
  yDown?: boolean;
  width?: number;
  height?: number;
  padding?: { top: number; right: number; bottom: number; left: number };
  /** Render the plot content given ready-made scales. */
  children: (scales: ChartScales) => ReactNode;
  /** Optional axis tick labels. */
  xLabel?: string;
  yLabel?: string;
  ariaLabel?: string;
}

const DEFAULT_PAD = { top: 16, right: 16, bottom: 34, left: 48 };

export function ChartFrame({
  title,
  xDomain = [0, 1],
  yDomain = [0, 1],
  yDown = false,
  width = 640,
  height = 320,
  padding = DEFAULT_PAD,
  children,
  xLabel,
  yLabel,
  ariaLabel,
}: ChartFrameProps) {
  const titleId = useId();
  const plot = {
    x: padding.left,
    y: padding.top,
    w: Math.max(1, width - padding.left - padding.right),
    h: Math.max(1, height - padding.top - padding.bottom),
  };

  const [x0, x1] = xDomain;
  const [y0, y1] = yDomain;
  const sx = (v: number) => plot.x + (x1 === x0 ? 0 : ((v - x0) / (x1 - x0)) * plot.w);
  const sy = (v: number) => {
    const t = y1 === y0 ? 0 : (v - y0) / (y1 - y0);
    return yDown ? plot.y + t * plot.h : plot.y + plot.h - t * plot.h;
  };

  return (
    <figure className="chart-frame" style={{ margin: 0 }}>
      {title && (
        <figcaption id={titleId} className="chart-title" style={{ color: "var(--head)", fontSize: 14, marginBottom: 6 }}>
          {title}
        </figcaption>
      )}
      <div className="chart-scroll" style={{ overflowX: "auto", width: "100%" }}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-labelledby={title ? titleId : undefined}
          aria-label={ariaLabel}
          style={{ maxWidth: "100%", display: "block" }}
        >
          {/* plot border / axes */}
          <rect
            x={plot.x}
            y={plot.y}
            width={plot.w}
            height={plot.h}
            fill="none"
            stroke="var(--line)"
            strokeWidth={1}
          />
          {children({ plot, sx, sy })}
          {xLabel && (
            <text x={plot.x + plot.w / 2} y={height - 6} textAnchor="middle" fill="var(--dim)" fontSize={11}>
              {xLabel}
            </text>
          )}
          {yLabel && (
            <text
              x={12}
              y={plot.y + plot.h / 2}
              textAnchor="middle"
              fill="var(--dim)"
              fontSize={11}
              transform={`rotate(-90 12 ${plot.y + plot.h / 2})`}
            >
              {yLabel}
            </text>
          )}
        </svg>
      </div>
    </figure>
  );
}
